import { DraftSurveyTemplateQuestion } from "../types";
import {
  useSurveyStore,
  selectSortedQuestions,
  hasValidationError
} from "../store";
import { PageCard } from "./sidebar-page-card";
import { cx } from "shared/utils";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  DragDropVerticalIcon,
  PlusSignCircleIcon,
  PlusSignIcon,
  KeyframesDoubleRemoveIcon
} from "@hugeicons-pro/core-stroke-rounded";

import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult
} from "@hello-pangea/dnd";

import {
  DraggableProvidedDraggableProps,
  DraggableProvidedDragHandleProps
} from "@hello-pangea/dnd";
import { forwardRef, useEffect } from "react";
import {
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant
} from "shared/components";
import { IconMap } from "../components/IconMap";

type DraggableProvidedProps = {
  draggableProps: DraggableProvidedDraggableProps;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  isDragging?: boolean;
};

const QuestionCard = forwardRef<
  HTMLDivElement,
  DraftSurveyTemplateQuestion &
    DraggableProvidedProps & { addNewQuestion: (afterIndex?: number) => void }
>(
  (
    { dragHandleProps, draggableProps, isDragging, addNewQuestion, ...props },
    ref
  ) => {
    const activeQuestionIdx = useSurveyStore(
      (state) => state.activeQuestionIdx
    );
    const updateActiveQuestionIdx = useSurveyStore(
      (state) => state.updateActiveQuestionIdx
    );
    const validationErrors = useSurveyStore((state) => state.validationErrors);
    const hasError = hasValidationError(
      validationErrors,
      "question",
      props.index
    );
    const { style, ...restDraggableProps } = draggableProps;
    return (
      <div
        ref={ref}
        id={`sidebar-question-${props.index}`}
        className={cx(
          "border relative bg-white rounded-md shadow-sm group h-[52px] px-4 py-2 flex items-center gap-2 select-none",
          hasError ? "border-danger-500" : "border-alpha-100"
        )}
        style={{
          ...style,
          transform: isDragging
            ? `${style?.transform ?? ""} rotate(5deg)`
            : style?.transform
        }}
        onClick={() => updateActiveQuestionIdx(props.index)}
        {...restDraggableProps}
      >
        <div className="px-3 py-1.5 border border-alpha-100 rounded-md flex gap-2 shrink-0">
          <span className="text-sm text-content-1000 font-medium">
            {props.index + 1}
          </span>
          <IconMap type={props.type} />
        </div>
        <p
          className={cx(
            "truncate text-sm transition-all",
            props.index === activeQuestionIdx
              ? "text-content-1000 font-bold"
              : "text-content-300 font-medium"
          )}
        >
          {props.question || "Question preview will be..."}
        </p>
        <div
          className="ml-auto shrink-0 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
          {...dragHandleProps}
        >
          <HugeiconsIcon
            icon={DragDropVerticalIcon}
            size={24}
            strokeWidth={2.5}
            className="text-content-1000"
          />
        </div>

        <div
          role="button"
          aria-description={`Add question block after question #${props.index + 1}`}
          className="absolute group-hover:block hidden -bottom-2.5 left-1/2 -translate-x-1/2 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            addNewQuestion(props.index + 1);
          }}
        >
          <HugeiconsIcon
            icon={PlusSignCircleIcon}
            size={20}
            className="text-content-200 bg-white rounded-full"
          />
        </div>
      </div>
    );
  }
);

QuestionCard.displayName = "QuestionCard";

export const SidebarQuestions = () => {
  const data = useSurveyStore((state) => state.data);

  const updateData = useSurveyStore((state) => state.updateData);

  const updateQuestionOrderByIdx = useSurveyStore(
    (state) => state.updateQuestionOrderByIdx
  );

  const addNewQuestion = useSurveyStore((state) => state.addNewQuestion);
  const questions = useSurveyStore(selectSortedQuestions);
  const validationErrors = useSurveyStore((state) => state.validationErrors);
  const nameError = hasValidationError(validationErrors, "survey", 0, "name");
  const activePage = useSurveyStore((state) => state.activePage);
  const activeQuestionIdx = useSurveyStore((state) => state.activeQuestionIdx);
  const setActivePage = useSurveyStore((state) => state.setActivePage);

  useEffect(() => {
    if (activeQuestionIdx === null) return;
    document
      .getElementById(`sidebar-question-${activeQuestionIdx}`)
      ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [activeQuestionIdx]);

  if (!data) return null;

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    updateQuestionOrderByIdx(result.source.index, result.destination.index);
  };

  return (
    <div className="max-w-[300px] flex flex-col h-[calc(100vh-72px)] w-full border-r border-alpha-80 bg-white">
      <div className="pt-6 px-4 shrink-0">
        <div className="pb-6 border-b border-alpha-40">
          <BaseInput
            id="title"
            maxLength={30}
            value={data.name || ""}
            onChange={(v) => {
              updateData({ ...data, name: v }, ["name"]);
            }}
            hideOptional
            error={nameError}
            label="Survey name"
          />
        </div>
      </div>
      <div className="flex-grow overflow-y-auto px-4 py-6 flex flex-col gap-4">
        <PageCard
          label="Start page"
          icon={KeyframesDoubleRemoveIcon}
          page="start"
          activePage={activePage}
          onClick={() => setActivePage("start")}
        />

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="list">
            {(provided) => (
              <ul
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="flex flex-col gap-4"
              >
                {questions.map((question, i) => (
                  <Draggable
                    key={question.internal_id}
                    draggableId={question.internal_id}
                    index={i}
                  >
                    {(provided, snapshot) => (
                      <QuestionCard
                        ref={provided.innerRef}
                        draggableProps={provided.draggableProps}
                        dragHandleProps={provided.dragHandleProps}
                        isDragging={snapshot.isDragging}
                        addNewQuestion={addNewQuestion}
                        {...question}
                      />
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>

        <PageCard
          label="End page"
          icon={KeyframesDoubleRemoveIcon}
          page="end"
          activePage={activePage}
          onClick={() => setActivePage("end")}
        />
      </div>

      <div className="px-6 py-4 border-t border-alpha-100">
        <Button
          className="w-full"
          HugeIcon={PlusSignIcon}
          size={ButtonSize.SM}
          variant={ButtonVariant.PRIMARY}
          onClick={() => addNewQuestion()}
        >
          Add question
        </Button>
      </div>
    </div>
  );
};
